
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function WhyAgents() {
  const features = [
    {
      icon: 'ri-tools-line',
      title: 'Use Tools and Systems',
      description: 'Agents connect to your helpdesk, CRM, billing systems, and internal databases to retrieve and act on real data.',
    },
    {
      icon: 'ri-route-line',
      title: 'Make Workflow Decisions',
      description: 'Follow defined logic paths based on ticket type, customer context, and escalation rules rather than simple pattern matching.',
    },
    {
      icon: 'ri-brain-line',
      title: 'Maintain Context',
      description: 'Track conversation history and customer state across interactions to provide consistent, informed responses.',
    },
    {
      icon: 'ri-user-voice-line',
      title: 'Escalate Intelligently',
      description: 'Recognize edge cases and complexity thresholds, routing to human agents with full context when needed.',
    }
  ];

  const comparisons = [
    { chatbot: 'Scripted responses', agent: 'Dynamic, context-aware actions' },
    { chatbot: 'No system access', agent: 'Integrated with CRM, billing, helpdesk' },
    { chatbot: 'Breaks on edge cases', agent: 'Escalates intelligently with context' },
    { chatbot: 'Stateless conversations', agent: 'Persistent memory across sessions' },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, getItemStyle } = useStaggerAnimation(features.length, { threshold: 0.1 });
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="bg-[#0F0F0F] py-16 md:py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#B8985F]/5 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#B8985F]/5 rounded-full blur-[100px]"></div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 relative z-10">
        <div
          ref={headerRef}
          className="text-center mb-10 md:mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#B8985F]/30 bg-[#B8985F]/10 mb-5 md:mb-6">
            <span className="text-[12px] tracking-[1.5px] uppercase text-[#B8985F] font-semibold">Agents vs Chatbots</span>
          </div>
          <h2 className="text-[28px] md:text-[40px] font-bold text-white mb-4 md:mb-5">
            Why Agents, Not Chatbots
          </h2>
          <p className="text-[15px] md:text-[17px] text-[#999] max-w-[680px] mx-auto leading-[1.6]">
            AI agents differ from traditional chatbots because they operate as functional components within your support infrastructure, not as conversational interfaces.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-[#181818] rounded-lg p-5 md:p-7 border border-[#252525] hover:border-[#B8985F]/30 transition-all duration-300 cursor-pointer group"
              style={getItemStyle(index)}
            >
              <div className="flex items-start gap-4 md:gap-5">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-lg bg-[#B8985F]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#B8985F]/20 transition-colors">
                  <i className={`${feature.icon} text-[#B8985F] text-lg md:text-xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[16px] md:text-[18px] font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-[13px] md:text-[15px] text-[#999] leading-[1.6]">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          ref={tableRef}
          className="mt-10 md:mt-16 bg-[#181818] rounded-lg border border-[#252525] p-5 md:p-8"
          style={{
            opacity: tableVisible ? 1 : 0,
            transform: tableVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s',
          }}
        >
          <div className="flex items-center gap-4 md:gap-6 mb-6 justify-center">
            <div className="flex-1 text-center max-w-[160px]">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#252525] flex items-center justify-center mx-auto mb-2 md:mb-3">
                <i className="ri-robot-line text-[#666] text-xl md:text-2xl"></i>
              </div>
              <p className="text-[12px] md:text-[14px] text-[#666] font-medium">Traditional Chatbot</p>
            </div>
            <div className="text-[20px] md:text-[24px] text-[#444]">vs</div>
            <div className="flex-1 text-center max-w-[160px]">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#B8985F]/15 flex items-center justify-center mx-auto mb-2 md:mb-3">
                <i className="ri-cpu-line text-[#B8985F] text-xl md:text-2xl"></i>
              </div>
              <p className="text-[12px] md:text-[14px] text-[#B8985F] font-medium">AI Support Agent</p>
            </div>
          </div>
          <div className="space-y-3">
            {comparisons.map((row, i) => (
              <div key={i} className="flex flex-col sm:flex-row items-center gap-1 sm:gap-6">
                <div className="flex-1 text-center sm:text-right w-full sm:w-auto">
                  <span className="text-[12px] md:text-[13px] text-[#555] line-through decoration-[#444]">{row.chatbot}</span>
                </div>
                <div className="hidden sm:block w-px h-5 bg-[#333]"></div>
                <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
                  <span className="text-[12px] md:text-[13px] text-[#ccc]">{row.agent}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
